import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import store from '../../../store';
import type { IRootDispatch, RootState } from '../../../store/models';
import { makeProfileMessageGenerator } from '../../../modules/referral/helpers';
import { WhoAmI } from '../../../store/CurrentUser.types';
import { ShareTarget } from '../../../modules/share';
import { ProviderReviewState } from '../../../store/ProviderReviews.model';
import { ProviderScheduleDay } from '../../../store/ProviderWorkSchedule.types';
import { RatingsSummary } from '../../../store/RatingsSummary.model';
import {
  Stack,
  PortalOverlay,
  HeaderText,
} from '../../ui';
import { selectors as userSelectors } from '../../../store/CurrentUser.model';
import {
  useAssignTests,
  useIsMobile,
  useLinkTo,
  useGoToChatWithPro,
  useUserStateRedux,
  useMount,
} from '../../../hooks';
import { ProBio } from './ProBio/ProBio';
import analytics from '../../../modules/analytics';
import useParams from '../../../hooks/useParams';
import useProviderRecommendations from './hooks/useProviderRecommendations';

import { SearchPopoverModal } from '../../consumer/search/SearchPopover';

import { ProfileImageGridContainer } from './ProfileImageGrid/ProfileImageGridContainer';
import { SidebarInfo } from './SidebarInfo';
import { ShareProps } from '../../consumer/sharing';
import { useEnterClientInIncentivesForOneAppointment } from '../../consumer/incentives/isInIncentivesTest';
import { RateThisAppModal, useRateThisAppOpen } from '../RateThisApp';
import { useShowReferralLandingIfNeeded } from '../../consumer/referrals/useShowReferralLandingIfNeeded';
import ProfileSentimentSurvey, { useProfileSentimentSurvey } from './ProfileSentimentSurvey';

import { useIncentivesPopovers } from '../../consumer/incentives/IncentivesPopover';
import { REFERRAL_SHARE_KEY } from '../../../store/ShowIncentives.model';

import { COM_806_TEST_NAME, COM_806_TEST_PERCENT } from '../../consumer/booking/ClientBookingSurvey/constants';

import ResponsiveProfileLayout from './layout/ResponsiveProfileLayout/ResponsiveProfileLayout';

import { CLIENT_INVITED_TO_BOOK, PROFILE_ROUTE } from '../../../route-names';
import { ClientProviderProfile } from '../../../api/ProfileClientView';
import { ProviderServiceGroup } from '../../../api/Providers/ServiceGroups';
import { IGalleryImage } from '../../../api/Providers/GalleryImages';
import { ProviderService } from '../../../api/Providers/Services';
import { SS_MESSAGING_SMS_OPTED_IN } from './constants';

import { flattenServiceGroups } from '../../../modules/provider/loadServiceGroups';
import { useMarketplaceProfile } from './MarketplaceProfile/hooks/useMarketplaceProfile';
import { AuthOrigins } from '../../../modules/user/constants';
import { NotifyMeRequestForChatModal } from '../Chat';
import { useIsStylieAwardee } from '../../../modules/stylies/utils';
import { TopProPopover } from '../TopPro/TopProPopover';
import { ProviderActionButtonsContainer } from './ProBio/ProviderActions/ProActionButtonsContainer';
import { getWidth, listenToWidth } from '../../../modules/Responsive';
import { buildPreviouslyBookedServices } from './serviceSelectors';
import { ServiceList } from './ServiceList/ServiceList';
import { useTriggerLoginModal } from '../login/LoginModalProvider';
import { LoginModalMode } from '../login/LoginModalProvider/LoginModalProvider';
import MarketplaceProfile from './MarketplaceProfile';
import { MarketplaceProfileModelState } from '../../../store/MarketplaceProfile/MarketplaceProfile.types';
import { TopServicesList } from './ServiceList/TopServiceList';
import { ProviderServicePromotion } from '../../../api/Providers/Promotions';
import { PromotedServiceItem } from './ServiceItem/PromotedServiceItem';
import { ProviderProfileLastBooked } from './ProviderProfileLastBooked';
import { ProviderAllServiceReviewsSummaryState } from '../../../store/ProviderReviewsSummary.model';

export type ProviderProfileProps = {
  profile: ClientProviderProfile;
  onBookmarkClick: () => void;
  reviews: ProviderReviewState;
  reviewsSummary: ProviderAllServiceReviewsSummaryState;
  ratingsSummary: RatingsSummary;
  schedule: ProviderScheduleDay[];
  serviceGroups: ProviderServiceGroup[];
  galleryImages: IGalleryImage[];
  bookmarked: boolean;
  marketplaceProfile: MarketplaceProfileModelState;
  isOwnProfile?: boolean;
};

export const ProviderProfile = ({
  profile,
  reviews,
  reviewsSummary,
  ratingsSummary,
  schedule,
  serviceGroups,
  galleryImages,
  bookmarked,
  onBookmarkClick,
  marketplaceProfile,
  isOwnProfile,
}: ProviderProfileProps) => {
  const [showTopProPopover, setShowTopProPopover] = useState<boolean>(false);
  const [currentScreenWidth, setCurrentScreenWidth] = useState(getWidth);

  const MIN_LAST_SERVICES = 1;

  const currentUser = useSelector<RootState, RootState['user']>(
    state => state.user,
    (a, b) => a.userId === b.userId,
  );
  const isLoggedIn = !currentUser.is_anon;

  const previouslyBookedServices = useSelector<RootState, ProviderService[] | null>(
    state => buildPreviouslyBookedServices(state, profile),
  );

  const userId = useSelector(userSelectors.getId);
  const dispatch = useDispatch<IRootDispatch>();

  useEffect(() => {
    if (isLoggedIn && userId && profile?.id) {
      dispatch.previousAppointments.load({
        userId,
        providerId: profile.id,
      });
    }
  }, [
    isLoggedIn,
    userId,
    profile,
    dispatch.previousAppointments,
  ]);

  const providerRecommendation = useProviderRecommendations(profile.id);
  const services = useMemo(() => flattenServiceGroups(serviceGroups), [serviceGroups]);
  const showMarketplace = marketplaceProfile?.shouldRender && providerRecommendation?.found;

  useAssignTests([
    {
      name: COM_806_TEST_NAME,
      percent: COM_806_TEST_PERCENT,
    },
  ]);

  const routeParams = useParams();
  const linkTo = useLinkTo();
  const isMobile = useIsMobile();
  const reviewCount = reviews?.total || 0;
  const ratingCount = ratingsSummary?.total || 0;

  const trackingId = useSelector<RootState, string>(
    state => state.trackingId.trackingId,
  );

  const showProfileLaunchSurvey = useProfileSentimentSurvey(profile);

  const {
    isIncentivesSearchOpen,
    setIncentivesSearchOpen,
    handleIncentivesSearchSubmitted,
    handleIncentivesSearchDismissed,
  } = useIncentivesPopovers();

  const promotedServices = useSelector<RootState, ProviderService[]>(
    state => state.profileServiceGroups?.[profile.id]?.promotedServices || [],
  );
  const promoServiceMap = useSelector<RootState, Record<number, ProviderServicePromotion[]>>(
    state => state.profileServiceGroups?.[profile.id]?.promotionsByServiceId || {},
  );
  type PromotionData = {
    promo: ProviderServicePromotion | undefined;
    serviceId: number;
  };

  let promotionData: PromotionData[] = [];
  if (promotedServices.length > 0) {
    const serviceMap = {};
    promotionData = promotedServices.map(promotedService => ({
      ...serviceMap[promotedService.id],
      promo: promoServiceMap[promotedService.id]?.[0],
      serviceId: promotedService.id,
    }));
  }

  // COM-937
  const shouldShowChat = useSelector(
    store.select.chat.selectIsProChatEnabledById(profile.id),
  );

  useMount(() => {
    // populate data required to determine top services
    dispatch.profileServiceGroups.loadProvider({ providerId: profile.id, loadPromotions: true });
  });

  const logUserIn = useTriggerLoginModal();
  const goToChatWithPro = useGoToChatWithPro(profile);
  const [shouldRedirectToChat, setShouldRedirectToChat] = useState(false);
  const clientPhoneNumber = currentUser.phone_number;
  const [showUpdatePhoneNumberModal, setShowUpdatePhoneNumberModal] = useState<boolean>(false);
  const handleChatModalClose = () => setShowUpdatePhoneNumberModal(false);
  const [
    alreadyOptedIn, ,userStateLoaded,
  ] = useUserStateRedux(SS_MESSAGING_SMS_OPTED_IN);

  const showLastBooked = previouslyBookedServices?.length > MIN_LAST_SERVICES && isLoggedIn;

  useEffect(() => {
    if (shouldRedirectToChat) {
      if (userStateLoaded && clientPhoneNumber && alreadyOptedIn) {
        goToChatWithPro();
      } else if (userStateLoaded && !alreadyOptedIn) {
        setShowUpdatePhoneNumberModal(true);
      }
    }
  }, [
    shouldRedirectToChat,
    goToChatWithPro,
    userStateLoaded,
    alreadyOptedIn,
    clientPhoneNumber,
  ]);

  const handleMessageClicked = async () => {
    if (isLoggedIn) {
      if (alreadyOptedIn) {
        setShouldRedirectToChat(true);
      } else {
        setShowUpdatePhoneNumberModal(true);
      }
    } else {
      const loggedInResponse = await logUserIn({
        mode: LoginModalMode.Login,
        origin: AuthOrigins.ProProfileMessageClick,
        loginRequired: true,
      });

      if (loggedInResponse?.userData?.phone_number) {
        setShouldRedirectToChat(true);
      } else {
        setShowUpdatePhoneNumberModal(true);
      }
    }
  };

  useMarketplaceProfile({ providerId: profile?.id });

  useEffect(() => {
    const {
      forwardToBook,
      source,
      time,
      date,
      serviceId,
      allServices,
    } = routeParams;
    /*
     * [ATL-4344] route to booking flow if all above params exist and are valid.
     * The "forwardToBook" comes from a SMS shortened link to the profile page.
     * We don't currently expose an endpoint that shortens links to the booking flow.
     */
    if (
      Boolean(forwardToBook)
      && !!source
      && !!time
      && !!date
      && (!!serviceId || !!allServices)
    ) {
      linkTo(CLIENT_INVITED_TO_BOOK, {
        providerId: profile.id,
        serviceId,
        allServices,
        time,
        date,
        source,
      });
    }
  }, [routeParams]);

  useEffect(() => {
    const handleResize = () => setCurrentScreenWidth(getWidth());
    return listenToWidth(handleResize);
  });

  // Add to the incentives stack based on test name
  const enterClientIncentivesForReferral = useEnterClientInIncentivesForOneAppointment(
    REFERRAL_SHARE_KEY,
  );
  // On successful share action
  const handleReferralShare = () => {
    // Opt sharer into incentives
    enterClientIncentivesForReferral();
  };

  const generateProfileMessage = makeProfileMessageGenerator(profile);

  const shareConfig: Omit<ShareProps, 'user'> = {
    message: generateProfileMessage(currentUser as WhoAmI, trackingId),
    target: ShareTarget.PROFILE,
    provider: profile,
    onShareComplete: handleReferralShare,
  };

  useShowReferralLandingIfNeeded(
    {
      target: ShareTarget.PROFILE,
      providerId: profile.id,
    },
    {
      view_id: 'client_proprofile_client_referral_popover_viewed',
      click_id: 'client_proprofile_client_referral_popover_click',
      dismiss_id: 'client_proprofile_client_referral_popover_dismiss',
    },
    () => setIncentivesSearchOpen(true),
  );

  const {
    isLoading: isLoadingStyliesData,
  } = useIsStylieAwardee(profile);

  const onMessagePro = async () => {
    analytics.track('client_proprofile_message_click', {
      ...routeParams,
      profile_type: 'provider',
      provider_id: profile.id,
    });

    handleMessageClicked();
  };

  const onProfilePhotoClick = () => {
    analytics.track('client_proprofile_profile_pic_click', {
      provider_id: profile.id,
      image_url: profile.profile_photo,
    });
    linkTo(PROFILE_ROUTE, { providerId: profile.vanity_url });
  };

  const { handleClose: handleCloseRateThisApp, isRateThisAppOpen } = useRateThisAppOpen(null);

  const handleTopProClick = useCallback(() => {
    analytics.track('click', {
      component: 'top_pro_badge',
      provider_id: profile.id,
    });
    setShowTopProPopover(true);
  }, [profile.id]);

  const handleTopProPopoverClose = () => {
    setShowTopProPopover(false);
  };

  const handleRatingsPhotoClick = (service: ProviderService & { isTopService?: boolean }) => {
    analytics.track('client_servicereview_clicked', {
      provider_id_viewed: profile.id,
      service_id: service.id,
      service_name: service.name,
      is_top_service: service.isTopService,
    });

    linkTo('profile.service_info', {
      providerId: profile.id,
      serviceId: service.id,
    });
  };

  if (isLoadingStyliesData) {
    return null;
  }

  const inNewDesktopBreakpoint = currentScreenWidth <= 900;
  const inNewMobileBreakpoint = currentScreenWidth <= 744;
  const hasImages = galleryImages.length;
  const showBottomSlot = isMobile || inNewMobileBreakpoint;

  return (
    <React.Fragment>
      <ResponsiveProfileLayout
        inNewDesktopBreakpoint={inNewDesktopBreakpoint}
        inNewMobileBreakpoint={inNewMobileBreakpoint}
        topSlot={(
          <Stack
            testID="topSlot-revamp"
          >
            <Box
              testID="pro-bio-revamp"
              paddingY={(isMobile || inNewMobileBreakpoint) ? 0 : 48}
            >
              <ProBio
                isEditMode={false}
                isTopPro={profile.is_top_pro}
                isOwnProfile={false}
                provider={profile}
                ratingCount={ratingCount}
                reviewCount={reviewCount}
                isBookmarked={bookmarked}
                onProfilePhotoClick={onProfilePhotoClick}
                onBookmarkClick={onBookmarkClick}
                shareConfig={shareConfig}
                onTopProClick={handleTopProClick}
              />
            </Box>
            {/* gallery revamp */}
            {hasImages && (
              <ProfileImageGridContainer
                isOwnProfile={isOwnProfile}
                profile={profile}
                isEditMode={false}
                galleryImages={galleryImages}
                inNewDesktopBreakpoint={inNewDesktopBreakpoint}
                inNewMobileBreakpoint={inNewMobileBreakpoint}
                isMobile={isMobile}
              />
            )}
          </Stack>
        )}
        leftColumnSlot={(
          <React.Fragment>
            <Box paddingTop={24} paddingBottom={24} paddingLeft={isMobile ? 24 : 0}>
              <ProviderActionButtonsContainer
                providerId={profile.id}
                onMessageClicked={shouldShowChat ? handleMessageClicked : onMessagePro}
              />
            </Box>
            {/* Last Booked Services */}
            {showLastBooked && (
              <Box paddingBottom={24}>
                <ProviderProfileLastBooked
                  profile={profile}
                  previouslyBookedServices={previouslyBookedServices || []}
                  isMobile={isMobile}
                  handleRatingsPhotoClick={handleRatingsPhotoClick}
                />
              </Box>
            )}
            {/* Promoted Services */}
            {promotionData?.length > 0 && (
              <Stack
                paddingBottom={24}
                paddingX={isMobile ? 24 : 0}
                testID="promoted-services-revamp"
              >
                <React.Fragment>
                  <HeaderText level={isMobile ? 3 : 2} testID="promoted-services-label">Promotions</HeaderText>
                  {promotionData.map(promotion => {
                    const service = promotedServices.find(
                      serviceToFind => serviceToFind.id === promotion.serviceId,
                    ) as ProviderService;
                    return (
                      <Stack>
                        <PromotedServiceItem
                          key={promotion.serviceId}
                          service={service}
                          providerId={profile.id}
                          providerPhone={profile.public_phone_number}
                          isOnlineBookable={(
                            profile.is_effectively_online_bookable
                            && !profile.is_disabled
                            && !!service.is_online_bookable
                          )}
                          showAndUp={service.cost_and_up || profile.smart_pricing_enabled}
                          promotion={promotion.promo}
                        />
                      </Stack>
                    );
                  })}
                </React.Fragment>

              </Stack>
            )}
            <Stack
              testID="top-services-revamp"
            >
              {/* Top Services */}
              <TopServicesList
                profile={profile}
                services={services}
                onRatingsPhotoClick={handleRatingsPhotoClick}
              />
              {/* Service List */}
              <ServiceList
                profile={profile}
                onRatingsPhotoClick={handleRatingsPhotoClick}
                hasMarketplaceProfile={showMarketplace}
                reviewsSummary={reviewsSummary}
              />
            </Stack>
          </React.Fragment>
        )}
        rightColumnSlot={(
          <Box
            paddingTop={24}
            testID="right_colum_slot_revamp"
          >
            <Box
              borderWidth={1}
              borderColor={BrandColor.LightGrey}
              borderStyle="solid"
              borderRadius={16}
            >
              <SidebarInfo
                profile={profile}
                isEditMode={false}
                onMessagePro={onMessagePro}
                schedule={schedule}
                isTopPro={profile.is_top_pro}
                provider={profile}
              />
            </Box>
          </Box>
        )}
        bottomSlot={showBottomSlot && (
          <MarketplaceProfile
            marketplaceProfile={marketplaceProfile}
            providerRecommendationData={providerRecommendation}
            providerLatitude={profile?.coords?.latitude}
            providerLongitude={profile?.coords?.longitude}
            providerCity={profile?.city}
          />
        )}
      />
      {/* MODALS */}
      {isIncentivesSearchOpen && (
        <SearchPopoverModal
          showIncentivesStar
          source="incentives_profile_search"
          onSubmit={handleIncentivesSearchSubmitted}
          onClose={handleIncentivesSearchDismissed}
        />
      )}
      {isRateThisAppOpen && (
        <RateThisAppModal onClose={handleCloseRateThisApp} />
      )}
      {showProfileLaunchSurvey ? (
        <ProfileSentimentSurvey profile={profile} />
      ) : null}
      {showUpdatePhoneNumberModal && (
        <PortalOverlay name="booking-notify-me-request-modal">
          <NotifyMeRequestForChatModal
            user={currentUser}
            headerText={(
              <HeaderText level={1} testID="client_notify_me_for_chat_modal_bell_header">
                Get notified when
                <HeaderText level={1} color={BrandColor.DarkBlue}>
                  {` ${profile.name} `}
                </HeaderText>
                responds
              </HeaderText>
            )}
            trackingParams={{
              type: 'date-selection',
              provider_id: profile.id,
            }}
            onClose={handleChatModalClose}
            onSuccess={goToChatWithPro}
          />
        </PortalOverlay>
      )}
      {showTopProPopover && (
        <TopProPopover
          providerName={profile.name}
          onBackdropClick={handleTopProPopoverClose}
          onClose={handleTopProPopoverClose}
        />
      )}
    </React.Fragment>
  );
};
